// IE Hub - Profile Dialog Component
// Full-page dialog on mobile, dropdown on desktop showing user profile and account options

import {
  Box,
  CloseIcon,
  Drawer,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme
} from '../ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_VERSION_INFO } from '../shared/version';
import { DashboardService } from '../shared/api/services';
import { ActionType } from '../shared/api/types';
import {
  AccountCircleOutlined as AccountCircleIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  HeadsetMic as SupportAgentIcon,
  Email as CallIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { RiBankLine as BankIcon } from "react-icons/ri";
import { LuHandshake } from "react-icons/lu";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  userEmail?: string | undefined;
  onLogout: () => void;
  onContactUsClick?: (() => void) | undefined;
  onDeleteAccountClick?: (() => void) | undefined;
  /**
   * When true, hide all navigation options (except Logout) to enforce the
   * creditor Open Banking-only experience.
   */
  openBankingOnly?: boolean | undefined;
  anchorEl?: HTMLElement | null | undefined;
}

interface MenuItem {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  color?: string;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({ 
  open, 
  onClose, 
  onLogout,
  onContactUsClick,
  onDeleteAccountClick,
  openBankingOnly,
  anchorEl 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    const currentPath = location.pathname;

    // If the user is on budget or documents, request those pages to save
    // before navigating away (mirrors header behaviour).
    if (typeof window !== "undefined") {
      if (currentPath === "/update-budget") {
        window.dispatchEvent(
          new CustomEvent("iehub:budget-before-leave", {
            detail: { targetPath: path },
          })
        );
        onClose();
        return;
      }

      if (currentPath === "/documents") {
        window.dispatchEvent(
          new CustomEvent("iehub:documents-before-leave", {
            detail: { targetPath: path },
          })
        );
        onClose();
        return;
      }
    }

    navigate(path);
    onClose();
  };

  const menuItems: MenuItem[] = openBankingOnly
    ? []
    : [
        { 
          icon: <AccountCircleIcon sx={{ fontSize: 18 }} />, 
          label: 'My profile', 
          onClick: () => handleNavigation('/profile') 
        },
        { 
          icon: <BankIcon size={18} />, 
          label: 'Open banking service', 
          onClick: () => handleNavigation('/open-banking') 
        },
        {
          icon: <LuHandshake size={18} />,
          label: 'Our Partners',
          onClick: () => handleNavigation('/help-and-support')
        },
        {
          icon: <SupportAgentIcon sx={{ fontSize: 18 }} />,
          label: "Support and FAQ's",
          onClick: () => handleNavigation('/support-faqs')
        },
      ];

  // Profile content component (drawer content)
  const drawerContent = (
    <Box data-testid="components-drawer-content-container-action-1"
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close Button */}
      <Box data-testid="components-drawer-content-container-action-2"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
          pb: 1
        }}
      >
        <IconButton data-testid="components-drawer-content-btn-on-close" onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: '14.724px' }} />
        </IconButton>
      </Box>

      {/* Menu Items Container */}
      <Box data-testid="components-drawer-content-container-action-3"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: 2,
          pb: 3
        }}
      >
        {/* Top Menu Items */}
        <Box data-testid="components-drawer-content-container-action-4" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {menuItems.map((item) => (
            <Box data-testid="components-drawer-content-container-action-5"
              key={item.label}
              onClick={() => {
                DashboardService.logButtonAction({
                  actionType: ActionType.BUTTON_CLICK,
                  actionName: `Profile Menu: ${item.label}`,
                  pageName: "Global",
                  subSection: "ProfileDialog",
                }).catch(() => {});
                item.onClick();
                onClose();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Box data-testid="components-drawer-content-container-action-6" sx={{ color: item.color || '#000', display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </Box>
              <Typography data-testid="components-drawer-content-text-action"
                sx={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#000',
                  letterSpacing: '-0.304px',
                  lineHeight: '22.77px'
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Bottom Section: Logout + Version */}
        <Box data-testid="components-drawer-content-container-action-7" sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          {/* Logout Button */}
          <Box data-testid="components-drawer-content-container-on-logout"
            onClick={() => {
              DashboardService.logButtonAction({
                actionType: ActionType.BUTTON_CLICK,
                actionName: "Logout",
                pageName: "Global",
                subSection: "ProfileDialog",
              }).catch(() => {});
              onLogout();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(239, 83, 80, 0.08)'
              }
            }}
          >
            <Box data-testid="components-drawer-content-container-action-8" sx={{ color: '#EF5350', display: 'flex', alignItems: 'center' }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography data-testid="components-drawer-content-text-logout"
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#EF5350',
                letterSpacing: '-0.304px',
                lineHeight: '22.77px'
              }}
            >
              Logout
            </Typography>
          </Box>

          {/* Version Information */}
          <Box data-testid="components-drawer-content-container-action-9"
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Typography data-testid="components-drawer-content-text-version"
              sx={{
                fontSize: '12px',
                color: '#757575',
                letterSpacing: '-0.2px'
              }}
            >
              Version {APP_VERSION_INFO.version}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Mobile: Drawer from right
  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            backgroundColor: '#ffffff',
            boxShadow: 'none',
          }
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: Dropdown/Popover
  return (
    <Popover
      open={open}
      anchorEl={anchorEl || null}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        mt: 1,
        '& .MuiPopover-paper': {
          width: 320,
          maxWidth: '90vw',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #cacaca'
        }
      }}
    >
      {drawerContent}
    </Popover>
  );
};

export default ProfileDialog;
